import { IconDots } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { Row } from '@tanstack/react-table';
import {
    useState, useEffect, useRef, useCallback,
} from 'react';
import './DraggableTableActionMenu.scss';
import { useNavigate } from 'react-router-dom';
import { Deal } from 'views/Deals/types';
import DeleteDealModal from 'views/Deals/DealsTable/BulkActions/DeleteDealModal';
import {
    DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown,
} from 'reactstrap';

interface DraggableTableActionMenuProps<TData> {
    row: Row<TData>;
}

export const DraggableTableActionMenu = <TData extends object>({ row }: DraggableTableActionMenuProps<TData>) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleDeleteModal = useCallback(() => {
        setShowDeleteModal(prev => !prev);
        setIsOpen(false);
    }, []);

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            window.document.addEventListener('mousedown', handleClickOutside);
        } else {
            window.document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            window.document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleViewDeal = useCallback(
        (deal: Deal) => () => {
            navigate(`/deal/${deal.id}`);
        },
        [navigate],
    );

    const handleDeleteDeal = () => {
        toggleDeleteModal();
    };

    return (
        <div ref={menuRef} className="DraggableTableActionMenu">
            <UncontrolledDropdown>
                <DropdownToggle
                    tag="div"
                    onClick={e => {
                        e.stopPropagation();
                        setIsOpen(prev => !prev);
                    }}
                >
                    <NSIcon icon={IconDots} className="cursor--pointer" role="button" />
                </DropdownToggle>
                <DropdownMenu container="body" className="p-0 m-0 z-index-100">
                    <DropdownItem
                        id={`view-deal-${(row.original as Deal).id}`}
                        onClick={handleViewDeal(row.original as Deal)}
                        className="DraggableTableActionMenu__dropdown__item cursor--pointer heap-deal-pipeline-view-deal"
                    >
                        View deal
                    </DropdownItem>
                    <DropdownItem
                        id={`delete-deal-${(row.original as Deal).id}`}
                        onClick={handleDeleteDeal}
                        className="DraggableTableActionMenu__dropdown__item cursor--pointer heap-deal-pipeline-delete-deal"
                    >
                        Delete deal
                    </DropdownItem>
                </DropdownMenu>
            </UncontrolledDropdown>

            <DeleteDealModal toggle={toggleDeleteModal} isOpen={showDeleteModal} deals={[row.original as Deal]} />
        </div>
    );
};
